<script setup lang="ts" generic="T">
import { computed, ref, type Component } from 'vue';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { LazyTooltip } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/vue/24/solid';
import FilterOptionDisplay from '@/components/FilterBuilder/FilterOptionDisplay.vue';

const props = withDefaults(defineProps<{
  modelValue?: string[];
  options: T[];
  getValue: (option: T) => string;
  getLabel: (option: T) => string;
  /** Filter options by search query. Default: label (case-insensitive) includes query. */
  filterByQuery?: (option: T, query: string) => boolean;
  isLoading?: boolean;
  placeholder?: string;
  disabled?: boolean;
  /** Prefix for data-testid attributes (e.g. 'filter-party-ids-combobox') */
  testIdPrefix?: string;
  /**
   * Optional component to render each option in the list, and the closed control
   * when exactly one option is chosen. Receives `{ value, label, option, size }`.
   * Use to surface coloured badges or marks (severity, rating, bucket type)
   * without duplicating the styling. The trigger wraps it in a holder that clips,
   * so a long label is cut off rather than escaping the control, and the
   * component may add its own `truncate` for a nicer ellipsis.
   */
  optionComponent?: Component;
}>(), {
  modelValue: () => [],
  placeholder: 'Select...',
  disabled: false,
  isLoading: false,
  testIdPrefix: 'filter-multi-combobox',
  filterByQuery: undefined,
  optionComponent: undefined,
});

const emit = defineEmits<{
  'update:modelValue': [value: string[]];
}>();

const searchQuery = ref('');

const defaultFilterByQuery = (option: T, searchQuery: string) => {
  const label = props.getLabel(option).toLowerCase();
  return label.includes(searchQuery.toLowerCase());
};

const optionFilterByQuery = computed(() => props.filterByQuery ?? defaultFilterByQuery);

const filteredOptions = computed(() => {
  if (!props.options.length) return [];
  if (!searchQuery.value) return props.options;
  const currentSearchQuery = searchQuery.value;
  return props.options.filter((option) => optionFilterByQuery.value(option, currentSearchQuery));
});

const isSelected = (value: string) => props.modelValue.includes(value);

const toggleOption = (optionValue: string, checked: boolean) => {
  if (props.disabled) return;
  const selectedValues = [...props.modelValue];
  const valueIndex = selectedValues.indexOf(optionValue);
  if (checked && valueIndex === -1) selectedValues.push(optionValue);
  else if (!checked && valueIndex > -1) selectedValues.splice(valueIndex, 1);
  emit('update:modelValue', selectedValues);
};

const selectedOptions = computed(() =>
  props.options.filter((option) => isSelected(props.getValue(option)))
);

const displayText = computed(() => {
  if (props.modelValue.length === 0) return props.placeholder;
  if (props.modelValue.length === 1) {
    const option = selectedOptions.value[0];
    // Fall back to the raw value for a blank label too: showing the stored value
    // is more use than showing an empty control.
    return (option && props.getLabel(option)) || props.modelValue[0];
  }
  return `${props.modelValue.length} selected`;
});

/**
 * The one chosen option, when exactly one is chosen. Drives the closed control:
 * a single selection shows the same badge or mark the open list shows, so it does
 * not vanish the moment the dropdown closes. Two or more fall back to a count,
 * because several badges do not fit a trigger this size.
 *
 * Null when the chosen value is not in `options` (a saved filter naming a row that
 * has since gone), which leaves `displayText` to render the raw value.
 */
const singleSelectedOption = computed(() => {
  if (props.modelValue.length !== 1) return null;
  const option = selectedOptions.value[0];
  if (!option) return null;
  // An option component may condition its own root on having something to show
  // (TrackRatingBadge does), in which case it renders no element at all and the
  // text fallback beside it would be unreachable. Require a label, so a blank one
  // falls through to the text branch instead of emptying the control.
  return props.getLabel(option) ? option : null;
});

const tooltipText = computed(() =>
  selectedOptions.value.map((option) => props.getLabel(option)).join(', ')
);
</script>

<template>
  <!-- The tooltip lists what is picked. It wraps the whole menu, not just its
       trigger, so the tooltip and the menu each keep their own anchor. See
       LazyTooltip for why. -->
  <LazyTooltip side="top" align="start" content-class="max-w-xs" :enabled="modelValue?.length > 0">
    <DropdownMenu>
      <DropdownMenuTrigger as-child>
        <Button
          variant="outline"
          size="sm"
          :disabled="disabled || isLoading"
          class="w-full text-left text-xs font-normal pl-2 pr-4 [&>div]:w-full [&>div>div]:w-full [&>div>div]:justify-between"
          :data-testid="`${testIdPrefix}-trigger`"
        >
          <span
            data-slot="filter-option-holder"
            class="flex min-w-0 items-center overflow-hidden"
          >
            <FilterOptionDisplay
              v-if="optionComponent && singleSelectedOption"
              :component="optionComponent"
              :value="getValue(singleSelectedOption)"
              :label="getLabel(singleSelectedOption)"
              :option="singleSelectedOption"
              class="min-w-0"
            />
            <span v-else class="truncate min-w-0">{{ displayText }}</span>
          </span>
          <ChevronDownIcon class="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent class="w-56" align="start">
        <div class="p-2">
          <div class="relative">
            <MagnifyingGlassIcon class="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              v-model="searchQuery"
              placeholder="Search..."
              class="pl-8"
              :data-testid="`${testIdPrefix}-search`"
              @click.stop
            />
          </div>
        </div>
        <DropdownMenuCheckboxItem
          v-for="option in filteredOptions"
          :key="getValue(option)"
          :model-value="isSelected(getValue(option))"
          @update:model-value="(checked: boolean) => toggleOption(getValue(option), checked)"
          @select.prevent
          :data-testid="`${testIdPrefix}-option-${getValue(option)}`"
        >
          <FilterOptionDisplay
            v-if="optionComponent"
            :component="optionComponent"
            :value="getValue(option)"
            :label="getLabel(option)"
            :option="option"
          />
          <template v-else>{{ getLabel(option) }}</template>
        </DropdownMenuCheckboxItem>
        <div v-if="filteredOptions.length === 0 && !isLoading" class="px-2 py-1.5 text-sm text-muted-foreground">
          No options found
        </div>
        <div v-if="isLoading" class="px-2 py-1.5 text-sm text-muted-foreground">
          Loading...
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
    <template #content>
      <p>{{ tooltipText }}</p>
    </template>
  </LazyTooltip>
</template>
